import { useCallback, useMemo, useRef } from "react"
import { useTheme } from "@mui/material"
import { useNavigate } from "react-router-dom"

import { ImdfFeatureData } from "providers/venue/types"
import { useTranslation } from "providers/i18n"
import { useAppUI } from "providers/ui"
import { useFeatureDetail } from "hooks/feature/useFeatureDetail"

import { MotionDiv } from "components/motions"

import { Head } from "components/Head"
import { FeatureCard } from "components/domains/features"

import { usePlaceDetailMap } from "hooks/place/usePlaceDetailMap"
import { prepareTextByFieldName } from "utils/string"
import { StandardFeatureCard } from "components/domains/features/StandardFeatureCard"
import { useGeoLocation } from "providers/geolocation"

const FADE_IN_TRANSITION = {
  initial: { opacity: 0 },
  enter: { opacity: 1 },
}

export const PlaceDetailKioskVPage = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { dataLoaded, ...data } = useFeatureDetail()
  const {
    feature,
    name,
    id,
    categoryIconProp,
    boothName,
    location: locationName,
    level,
    isNavigatable = true,
  } = data
  const dialogRef = useRef(null)
  const dialogHeight = +dialogRef.current?.clientHeight || 0
  const { deviceLocation } = useGeoLocation()
  const { goBack } = useAppUI()

  const isReadyToNavigate = !!deviceLocation && isNavigatable

  const theme = useTheme()
  const { styleOverrides = {} } = theme.components[`AppDetailPage`] || {}
  const { root } = styleOverrides

  const { locationTextKeys, locationSeparator, locationEmpty, variant } =
    theme.components[`AppDetailPage`]?.defaultProps || {}

  usePlaceDetailMap(feature as ImdfFeatureData, {
    enabled: !!feature,
    offset: { bottom: dialogHeight },
  })

  const location = useMemo(() => {
    const locationTextObj = {
      locationNameText: t(locationName),
      boothNameText: boothName ? t("Booth Text", { name: t(boothName) }) : "",
      floorText: level ? t("Level Text", { level: t(level) }) : "",
    }
    return prepareTextByFieldName(locationTextObj, locationTextKeys, {
      empty: locationEmpty,
      separator: locationSeparator,
    })
  }, [
    t,
    locationTextKeys,
    locationSeparator,
    locationEmpty,
    locationName,
    boothName,
    level,
  ])

  const FeatureCardComponent = useMemo(() => {
    return variant !== "standardV1" ? FeatureCard : StandardFeatureCard
  }, [variant])

  const onNavigate = useCallback(() => {
    if (!isReadyToNavigate) return
    const locationId = feature?.properties?.feature?.id || id
    navigate(`/maps/dir/unknown/${locationId}`)
  }, [navigate, isReadyToNavigate, feature, id])

  const onViewPromotion = useCallback(() => {
    navigate(`/promotions/occupant/${id}`)
  }, [navigate, id])

  const onClose = useCallback(() => {
    goBack()
  }, [goBack])

  return (
    <>
      <Head title={`${t(name)} (${t("Place Detail")})`} />
      <MotionDiv
        sx={root}
        ref={dialogRef}
        initial="initial"
        animate="enter"
        transition={{ duration: 0.25, ease: [0.51, 0.92, 0.24, 1.15] }}
        variants={FADE_IN_TRANSITION}
      >
        <FeatureCardComponent
          open={true}
          onClose={onClose}
          loading={!dataLoaded}
          data={{ ...data, location }}
          onNavigate={onNavigate}
          onClickPromotionButton={onViewPromotion}
          categoryIconProp={categoryIconProp}
          isNavigatable={isReadyToNavigate}
        />
      </MotionDiv>
    </>
  )
}
